import React from 'react';
import { Card } from 'flowbite-react';
import { MultiLanguageFieldGroupProps } from '../types';
import { MultiLanguageField } from './MultiLanguageField';
import { useMultiLanguageInput } from '../hooks/useMultiLanguageInput';
import { useAppSelector } from '../../../../redux/hooks/useAppSelector';
import { selectIsDarkMode } from '../../../../redux/selectors/displaySettingsSelectors';

/**
 * MultiLanguageFieldGroup - Multiple related multi-language fields with shared language selector
 *
 * **Component Type:** Stateful (Uses internal hook for language selection)
 *
 * **Purpose:**
 * Renders multiple multi-language fields with a shared language selector at the top.
 * Reduces UI clutter when dealing with forms that have multiple language-aware fields.
 *
 * **State Management:**
 * - Internal: Language selection via useMultiLanguageInput hook
 * - External: Field values managed by parent via values prop
 *
 * **Usage:**
 * ```tsx
 * <MultiLanguageFieldGroup
 *   fields={[
 *     { id: 'title', label: 'context.title', type: 'text', validation: { maxLength: 50 } },
 *     { id: 'description', label: 'context.description', type: 'textarea', rows: 3 }
 *   ]}
 *   values={{
 *     'title': data.title || [],
 *     'description': data.description || []
 *   }}
 *   onChange={(fieldId, value) => onUpdate(fieldId, value)}
 *   sharedLanguageSelector={true}
 *   cardWrapper={true}
 *   t={t}
 * />
 * ```
 *
 * **When to Use:**
 * - Forms with 2+ multi-language fields
 * - All fields should use the same language at once
 * - Want cleaner UI without multiple language selectors
 *
 * @param props - Component props including fields config array and values
 * @returns React component
 */
export const MultiLanguageFieldGroup: React.FC<
  MultiLanguageFieldGroupProps
> = ({
  fields,
  values,
  onChange,
  onValidate,
  sharedLanguageSelector = true,
  cardWrapper = true,
  t,
  customRenderers = {},
  languageSelectorLabel,
}) => {
  const isDarkMode = useAppSelector(selectIsDarkMode);
  const { selectedLanguage, setSelectedLanguage } = useMultiLanguageInput();

  const handleFieldValidation = (
    fieldId: string,
    isValid: boolean,
    error?: string
  ) => {
    if (onValidate) {
      onValidate(fieldId, isValid, error);
    }
  };

  const content = (
    <>
      {fields.map((field, index) => (
        <MultiLanguageField
          key={field.id}
          config={{
            ...field,
            // Apply languageSelectorLabel to first field if shared selector is enabled
            languageSelectorLabel:
              sharedLanguageSelector && index === 0
                ? languageSelectorLabel || field.languageSelectorLabel
                : field.languageSelectorLabel,
          }}
          value={values[field.id] || []}
          onChange={(newValue) => onChange(field.id, newValue)}
          onValidate={(isValid, error) =>
            handleFieldValidation(field.id, isValid, error)
          }
          selectedLanguage={selectedLanguage}
          showLanguageDropdown={sharedLanguageSelector ? index === 0 : true}
          onLanguageChange={
            sharedLanguageSelector || index === 0
              ? setSelectedLanguage
              : undefined
          }
          isDarkMode={isDarkMode}
          t={t}
          customRenderer={customRenderers[field.id]}
          wrapInCard={false}
        />
      ))}
    </>
  );

  if (cardWrapper) {
    return <Card>{content}</Card>;
  }

  return <>{content}</>;
};
